import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Download, Info } from 'lucide-react';
import { toast } from 'sonner';
import RCPCHGrowthChart from '@/components/RCPCHGrowthChart';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function GrowthCharts() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [activeChart, setActiveChart] = useState('height');

  const loadData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/growth-data`);
      setChild(response.data.child);
      setMeasurements(response.data.measurements);
    } catch (error) {
      toast.error('Failed to load growth data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // First, save the report to documents
      const saveResponse = await axios.post(`${API}/children/${childId}/export-pdf/save`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (saveResponse.data?.document) {
        toast.success('Report saved to Media tab!');
        
        // Download the saved file
        const downloadResponse = await axios.get(`${API}/children/${childId}/documents/${saveResponse.data.document.id}/download`, {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', saveResponse.data.document.original_filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('Report downloaded successfully!');
      }
    } catch (error) {
      // Fallback to direct download
      try {
        const response = await axios.get(`${API}/children/${childId}/export-pdf`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${child.first_name}_${child.last_name}_Growth_Report.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        toast.success('Report downloaded successfully!');
      } catch (fallbackError) {
        toast.error('Failed to download report');
        console.error(fallbackError);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-500 animate-pulse">Loading growth charts...</div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gray-500">Child not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/child/${childId}`)}
            className="text-gray-400 hover:text-white mb-4 rounded-lg"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Profile
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white">
                UK-WHO Growth Charts
              </h1>
              <p className="text-gray-400 mt-2">
                {child.first_name} {child.last_name} • Clinical-Grade Growth Assessment
              </p>
            </div>
            <Button
              onClick={handleExportPDF}
              className="h-12 px-8 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-semibold rounded-full shadow-lg"
            >
              <Download className="mr-2 h-5 w-5" />
              Export Report
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {measurements.length === 0 ? (
          <div className="bg-zinc-900 rounded-3xl p-12 shadow-lg text-center">
            <Info className="h-16 w-16 text-[#02b5ff] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">No Measurements Yet</h3>
            <p className="text-gray-400 mb-6">Add measurements to view growth charts</p>
            <Button
              onClick={() => navigate(`/child/${childId}/add-measurement`)}
              className="h-12 px-8 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold rounded-full"
            >
              Add First Measurement
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Info Banner */}
            <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-start gap-4">
                <Info className="h-6 w-6 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold mb-2">About UK-WHO Growth Charts</h3>
                  <p className="text-[#02b5ff]/80 text-sm leading-relaxed">
                    These charts use the UK-WHO growth reference, combining WHO standards (0-4 years) and UK 1990 references (4+ years). 
                    They are the same charts used by NHS health professionals across the UK for monitoring child growth and development.
                    Measurements are plotted against age-specific centile curves to assess growth patterns.
                  </p>
                </div>
              </div>
            </div>

            {/* Chart Type Selector */}
            <div className="bg-zinc-900 rounded-2xl shadow-md p-2 inline-flex gap-2">
              <button
                onClick={() => setActiveChart('height')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  activeChart === 'height'
                    ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white shadow-md'
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                Height Chart
              </button>
              <button
                onClick={() => setActiveChart('weight')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  activeChart === 'weight'
                    ? 'bg-gradient-to-r from-[#02b5ff] to-[#0099dd] text-white shadow-md'
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                Weight Chart
              </button>
              <button
                onClick={() => setActiveChart('bmi')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  activeChart === 'bmi'
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md'
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                BMI Chart
              </button>
            </div>

            {/* RCPCH Chart */}
            <div>
              <RCPCHGrowthChart
                measurements={measurements}
                childData={child}
                chartType={activeChart}
              />
            </div>

            {/* Chart Legend */}
            <div className="bg-zinc-900 rounded-2xl shadow-md p-6">
              <h3 className="text-xl font-bold text-white mb-4">Understanding the Chart</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-zinc-800 rounded-xl p-4">
                  <h4 className="font-semibold text-white mb-2">Centile Curves</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    The curved lines represent different centiles (0.4th to 99.6th). If your child is on the 50th centile, 
                    50% of children the same age are shorter/lighter and 50% are taller/heavier.
                  </p>
                </div>
                <div className="bg-zinc-800 rounded-xl p-4">
                  <h4 className="font-semibold text-white mb-2">Normal Range</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    Most children (94%) will be between the 2nd and 98th centiles. Growth outside these lines 
                    may need review, but what matters most is consistent growth along a centile line.
                  </p>
                </div>
                <div className="bg-zinc-800 rounded-xl p-4">
                  <h4 className="font-semibold text-white mb-2">Your Child's Plot</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    Blue dots show measurements over time. Consistent growth parallel to centile lines is ideal. 
                    Crossing centile lines up or down may indicate growth changes worth discussing with a healthcare professional.
                  </p>
                </div>
                <div className="bg-zinc-800 rounded-xl p-4">
                  <h4 className="font-semibold text-white mb-2">Clinical Use</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    These charts are Class I Medical Devices used by NHS professionals. They help identify 
                    growth patterns, nutritional issues, and underlying health conditions through longitudinal monitoring.
                  </p>
                </div>
              </div>
            </div>

            {/* Measurement Summary */}
            <div className="bg-zinc-900 rounded-2xl shadow-md p-6">
              <h3 className="text-xl font-bold text-white mb-4">Recent Measurements</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-zinc-700">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Age</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Height (cm)</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Weight (kg)</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Height Centile</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Weight Centile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {measurements.slice(-5).reverse().map((m, idx) => (
                      <tr key={idx} className="border-b border-zinc-800 hover:bg-zinc-800">
                        <td className="py-3 px-4 text-sm text-white">
                          {new Date(m.measurement_date).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-white">{m.age_years.toFixed(1)}y</td>
                        <td className="py-3 px-4 text-sm font-semibold text-white">{m.standing_height}</td>
                        <td className="py-3 px-4 text-sm font-semibold text-white">{m.weight}</td>
                        <td className="py-3 px-4 text-sm text-[#02b5ff] font-medium">
                          {m.uk_who_percentiles?.height_percentile ? 
                            `${m.uk_who_percentiles.height_percentile.toFixed(1)}th` : 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm text-[#02b5ff] font-medium">
                          {m.uk_who_percentiles?.weight_percentile ? 
                            `${m.uk_who_percentiles.weight_percentile.toFixed(1)}th` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
